// src/pages/trash/TrashPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { TrashEntry, getTrashEntries, hardDeleteFromTrash, emptyTrash } from "../../services";
import { apiClient } from "../../services/apiClient";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import { Pagination } from "../../utils";
import { TRASH_PAGE_SIZE, TRASH_BULK_MIN } from "../../constants/settings.constants";
import { FaTrash, FaTrashAlt, FaUndo, FaChevronDown, FaChevronUp, FaTimes } from "react-icons/fa";
// ConfirmModal stays in components/common — two levels up from pages/trash
import { ConfirmModal } from "../../components/common/ConfirmModal";
import "./TrashPage.css";

// Group trash entries by topic for accordion display.
// Mirrors the DraftsPage groupByTopic pattern — one group per topic
// (match / person / club / team / custom topic), keyed by "{type}-{id}".
interface TopicGroup {
  key: string;   // "match-{id}" | "person-{id}" | "club-{id}" | "team-{id}" | "topic-{id}"
  label: string; // group caption shown in the accordion header
  url: string;   // link to the topic page
  entries: TrashEntry[];
}

const groupByTopic = (entries: TrashEntry[]): TopicGroup[] => {
  const map = new Map<string, TopicGroup>();

  for (const entry of entries) {
    let key: string;
    let label: string;
    let url: string;

    if (entry.matchId != null) {
      key = `match-${entry.matchId}`;
      label = `${entry.homeTeam} - ${entry.awayTeam}`;
      url = generateMatchUrlFromEntry(
        entry.homeTeam!,
        entry.awayTeam!,
        new Date(entry.matchDate!),
        entry.matchId
      );
    } else if (entry.personId != null) {
      key = `person-${entry.personId}`;
      label = entry.personFullName ?? `Kişi #${entry.personId}`;
      url = `/kisi/${entry.personSlug ?? entry.personId}`;
    } else if (entry.clubId != null) {
      key = `club-${entry.clubId}`;
      label = entry.clubName ?? `Kulüp #${entry.clubId}`;
      url = `/kulup/${entry.clubSlug ?? entry.clubId}`;
    } else if (entry.teamId != null) {
      key = `team-${entry.teamId}`;
      label = entry.teamCode
        ? `${entry.teamName ?? `Takım #${entry.teamId}`} ${entry.teamCode.toUpperCase()}`
        : entry.teamName ?? `Takım #${entry.teamId}`;
      url =
        entry.teamClubSlug && entry.teamSlug
          ? `/takim/${entry.teamClubSlug}/${entry.teamSlug}`
          : `/takim/${entry.teamId}`;
    } else if (entry.customTopicId != null) {
      key = `topic-${entry.customTopicId}`;
      label = entry.customTopicTitle ?? `Başlık #${entry.customTopicId}`;
      url = `/gundem/${entry.customTopicSlug ?? entry.customTopicId}`;
    } else {
      // Fallback — should not happen, but keeps the entry visible instead of dropping it
      key = `entry-${entry.id}`;
      label = "—";
      url = "/";
    }

    if (!map.has(key)) {
      map.set(key, { key, label, url, entries: [] });
    }
    map.get(key)!.entries.push(entry);
  }

  return Array.from(map.values());
};

export const TrashPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const [entries, setEntries] = useState<TrashEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  // Track which topic groups are collapsed (open by default)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Confirmation state for "empty all" action
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);

  // Selected entry IDs for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Track the single entry awaiting permanent delete confirmation (null = none)
  const [singleDeleteConfirmId, setSingleDeleteConfirmId] = useState<number | null>(null);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTrashEntries(page, TRASH_PAGE_SIZE);
      setEntries(response.content);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
      setSelectedIds(new Set());
    } catch (err) {
      setError(t("trash_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // Redirect if not logged in — placed after all hooks
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Toggle a single entry's selection
  const toggleSelect = (entryId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  // Select all / deselect all visible entries
  const toggleSelectAll = () => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map((e) => e.id)));
    }
  };

  // Bulk restore selected entries one by one
  const handleBulkRestore = async () => {
    setActionLoading(true);
    try {
      for (const id of Array.from(selectedIds)) {
        await apiClient.patch(`/api/entries/${id}/restore`);
      }
      setEntries((prev) => prev.filter((e) => !selectedIds.has(e.id)));
      setTotalItems((prev) => prev - selectedIds.size);
      setSelectedIds(new Set());
    } catch {
      setError(t("trash_restore_error"));
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk hard-delete selected entries one by one
  const handleBulkDelete = async () => {
    setActionLoading(true);
    setShowBulkDeleteConfirm(false);
    try {
      for (const id of Array.from(selectedIds)) {
        await hardDeleteFromTrash(id);
      }
      setEntries((prev) => prev.filter((e) => !selectedIds.has(e.id)));
      setTotalItems((prev) => prev - selectedIds.size);
      setSelectedIds(new Set());
    } catch {
      setError(t("trash_delete_error"));
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle accordion group open/closed
  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Restore a single entry (soft-delete → live again)
  const handleRestore = async (entryId: number) => {
    setActionLoading(true);
    try {
      await apiClient.patch(`/api/entries/${entryId}/restore`);
      // Optimistically remove from local list
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      setTotalItems((prev) => prev - 1);
    } catch {
      setError(t("trash_restore_error"));
    } finally {
      setActionLoading(false);
    }
  };

  // Permanently delete a single entry from trash (called after confirmation)
  const handleHardDelete = async (entryId: number) => {
    setSingleDeleteConfirmId(null);
    setActionLoading(true);
    try {
      await hardDeleteFromTrash(entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      setTotalItems((prev) => prev - 1);
    } catch {
      setError(t("trash_delete_error"));
    } finally {
      setActionLoading(false);
    }
  };

  // Empty all trash
  const handleEmptyTrash = async () => {
    setActionLoading(true);
    setShowEmptyConfirm(false);
    try {
      await emptyTrash();
      setEntries([]);
      setTotalItems(0);
      setTotalPages(0);
    } catch {
      setError(t("trash_empty_error"));
    } finally {
      setActionLoading(false);
    }
  };

  // Format a deletedAt ISO string to a readable date
  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const topicGroups = groupByTopic(entries);

  return (
    <div className="trash-container">
      {/* Page header */}
      <div className="trash-header">
        <div className="trash-header-title">
          <FaTrashAlt className="trash-header-icon" />
          <h1>{t("trash_title")}</h1>
        </div>
        {totalItems >= TRASH_BULK_MIN && (
          <div className="trash-header-actions">
            <span className="trash-count">
              {t("trash_entry_count", { count: totalItems })}
            </span>
            <button
              className="trash-empty-btn"
              onClick={() => setShowEmptyConfirm(true)}
              disabled={actionLoading}
            >
              <FaTrash />
              {t("trash_empty_all")}
            </button>
          </div>
        )}
      </div>

      <div className="trash-divider" />

      {/* Bulk action bar — visible when at least one entry is selected */}
      {selectedIds.size > 0 && (
        <div className="trash-bulk-bar">
          <span className="trash-bulk-count">
            {t("trash_selected_count", { count: selectedIds.size })}
          </span>
          <div className="trash-bulk-actions">
            <button
              className="trash-restore-btn"
              onClick={handleBulkRestore}
              disabled={actionLoading}
            >
              <FaUndo />
              {t("trash_restore_selected")}
            </button>
            <button
              className="trash-empty-btn"
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={actionLoading}
            >
              <FaTrash />
              {t("trash_delete_selected")}
            </button>
            <button
              className="trash-confirm-no"
              onClick={() => setSelectedIds(new Set())}
              disabled={actionLoading}
            >
              <FaTimes />
              {t("trash_deselect_all")}
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && <p className="trash-error">{error}</p>}

      {/* Loading state */}
      {loading ? (
        <p className="trash-placeholder">{t("loading")}</p>
      ) : entries.length === 0 ? (
        /* Empty state */
        <div className="trash-empty-state">
          <FaTrashAlt className="trash-empty-icon" />
          <p>{t("trash_empty_state")}</p>
        </div>
      ) : (
        /* Accordion groups by match */
        <div className="trash-groups">
          {/* Select-all row — only useful when there are multiple entries */}
          {entries.length >= TRASH_BULK_MIN && (
          <div className="trash-select-all-row">
            <label className="trash-checkbox-label">
              <input
                type="checkbox"
                checked={selectedIds.size === entries.length && entries.length > 0}
                onChange={toggleSelectAll}
              />
              {selectedIds.size === entries.length && entries.length > 0
                ? t("trash_deselect_all")
                : t("trash_select_all")}
            </label>
          </div>
          )}
          {topicGroups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.key);
            return (
              <div key={group.key} className="trash-group">
                {/* Group header */}
                <button
                  className="trash-group-header"
                  onClick={() => toggleGroup(group.key)}
                >
                  <div className="trash-group-title">
                    <Link
                      to={group.url}
                      className="trash-match-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {group.label}
                    </Link>
                    <span className="trash-group-count">
                      ({group.entries.length})
                    </span>
                  </div>
                  {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                </button>

                {/* Group entries */}
                {!isCollapsed && (
                  <div className="trash-group-entries">
                    {group.entries.map((entry) => (
                      <div
                        key={entry.id}
                        className={`trash-entry ${selectedIds.has(entry.id) ? "trash-entry-selected" : ""}`}
                      >
                        <div className="trash-entry-top">
                          {entries.length >= TRASH_BULK_MIN && (
                          <label className="trash-checkbox-label" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(entry.id)}
                              onChange={() => toggleSelect(entry.id)}
                            />
                          </label>
                          )}
                          <div className="trash-entry-content">{entry.content}</div>
                        </div>
                        <div className="trash-entry-footer">
                          <span className="trash-entry-date">
                            {t("trash_deleted_at")}: {formatDate(entry.deletedAt)}
                          </span>
                          <div className="trash-entry-actions">
                            <button
                              className="trash-restore-btn"
                              onClick={() => handleRestore(entry.id)}
                              disabled={actionLoading}
                              title={t("trash_restore")}
                            >
                              <FaUndo />
                              {t("trash_restore")}
                            </button>
                            <button
                              className="trash-delete-btn"
                              onClick={() => setSingleDeleteConfirmId(entry.id)}
                              disabled={actionLoading}
                              title={t("trash_delete_permanent")}
                            >
                              <FaTrash />
                              {t("trash_delete_permanent")}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={(page: number) => loadPage(page - 1)}
        />
      )}

      {/* Modal: empty all trash */}
      {showEmptyConfirm && (
        <ConfirmModal
          message={t("trash_empty_confirm")}
          confirmLabel={t("trash_confirm_yes")}
          cancelLabel={t("trash_confirm_no")}
          onConfirm={handleEmptyTrash}
          onCancel={() => setShowEmptyConfirm(false)}
          disabled={actionLoading}
        />
      )}

      {/* Modal: bulk permanent delete */}
      {showBulkDeleteConfirm && (
        <ConfirmModal
          message={t("trash_bulk_delete_confirm")}
          confirmLabel={t("trash_confirm_yes")}
          cancelLabel={t("trash_confirm_no")}
          onConfirm={handleBulkDelete}
          onCancel={() => setShowBulkDeleteConfirm(false)}
          disabled={actionLoading}
        />
      )}

      {/* Modal: single entry permanent delete */}
      {singleDeleteConfirmId !== null && (
        <ConfirmModal
          message={t("trash_bulk_delete_confirm")}
          confirmLabel={t("trash_confirm_yes")}
          cancelLabel={t("trash_confirm_no")}
          onConfirm={() => handleHardDelete(singleDeleteConfirmId)}
          onCancel={() => setSingleDeleteConfirmId(null)}
          disabled={actionLoading}
        />
      )}
    </div>
  );
};